import * as React from 'react';
import DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getFocusedFilterForTable from 'mini-mail-list-store/lib/utils/getFocusedFilterForTable';
import getMailListViewState from 'mini-mail-list-store/lib/utils/getMailListViewState';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';
import MailListEmptyState from './MailListEmptyState';
import MailListHeader from './MailListHeader';
import MailListViewState from 'mini-mail-list-store/lib/store/schema/MailListViewState';
import NativeAdUserFlight from 'mini-mail-nativead-userchecker/lib/NativeAdUserFlight';
import shouldShowAutomaticRepliesBanner from '../utils/mailList/shouldShowAutomaticRepliesBanner';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import { BINARY_SWITCH } from 'mini-mail-list-store/lib/store/schema/MailHeaderElementToFocus';
import { createAppContext } from 'mini-app-context';
import { EMAIL_CONTEXT_CHECKMARK } from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { emailContextValues } from 'mini-mail-bootstrap/lib/routing/mailViewHandler';
import { getBootType } from 'owa-config';
import { getNativeAdShowStatus } from 'mini-mail-nativead-userchecker/lib/getNativeAdShowStatus';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import { getStore } from 'mini-mail-nativeadlist-store/lib/store/store';
import { isConsumer } from 'owa-shared-start/lib/sessionDiagnostics';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { isOpx } from 'owa-opx';
import { LazyAutomaticRepliesBanner } from 'mini-mail-automatic-replies-banner-view';
import { LSRLogging } from 'mini-mail-reading-pane-view/lib/utils/logLSR';
import { MAIL_LIST_ITEMS_SELECTOR } from 'mini-mail-list-item/lib/components/MailListItem';
import { NativeAdContainer } from 'mini-mail-nativead-view';
import { observer } from 'mobx-react';
import { PerformanceDatapoint } from 'owa-analytics';
import { resetGlobalAdState } from 'mini-mail-nativead-view/lib/utils/getGlobalAdState';
import { safePerformanceMarkEnd } from 'mini-performance';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';
import { trace } from 'owa-trace';
import { triggerSearchSignals } from '../utils/telemetry/triggerSearchSignals';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';
import { VirtualizedMailList } from './VirtualizedListView';

import {
    DisplayCustomisationsProvider,
    RichContentFlags,
} from './VirtualizedListView/DisplayCustomisationsProvider ';
import {
    getCurrentlyFocusableListItemNode,
    resetFocusToFirstListItem,
} from 'dom-utils/lib/utils/listUtils';

let firstLoadComplete = false;

export interface MailListProps {
    isMailListShown: () => boolean;
    tableView: TableView;
}

@observer
export default class MailList extends React.Component<MailListProps, {}> {
    appContext = createAppContext();
    private switchRef = React.createRef<HTMLDivElement>();
    private filterRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        const tableView = this.props.tableView;
        if (tableView) {
            const mailListLoadState: MailListViewState = getMailListViewState();
            this.measureInitialLoadPerformance(tableView, mailListLoadState, 'mailListMount');
            resetFocusToFirstListItem(MAIL_LIST_ITEMS_SELECTOR);
        }
        if (emailContextValues.ECPath) {
            signalWorkflowEvent(
                EMAIL_CONTEXT_CHECKMARK,
                {
                    f: 'MailList', //limiting characters to save space
                },
                { isCore: true }
            );
            //A failure here (conversation/item reading pane not being hit) will lead to a timeout
        }
    }

    componentDidUpdate(prevProps: MailListProps) {
        const tableView = this.props.tableView;
        if (tableView) {
            const mailListLoadState: MailListViewState = getMailListViewState();
            const conversationsOrigin =
                prevProps.tableView && prevProps.tableView.id !== tableView.id
                    ? 'conversationsInStore'
                    : 'conversationsFetched';
            this.measureInitialLoadPerformance(tableView, mailListLoadState, conversationsOrigin);

            if (tableView.mailHeaderElementToFocus) {
                const element =
                    tableView.mailHeaderElementToFocus === BINARY_SWITCH
                        ? this.switchRef
                        : this.filterRef;
                trySetFocusOnElement(element);
            }
        }
        if (!getCurrentlyFocusableListItemNode(MAIL_LIST_ITEMS_SELECTOR)) {
            resetFocusToFirstListItem(MAIL_LIST_ITEMS_SELECTOR);
        }
    }

    private async measureInitialLoadPerformance(
        tableView: TableView,
        loadState: MailListViewState,
        source: string
    ) {
        try {
            if (!tableView || loadState === MailListViewState.Loading) {
                // We don't have the right info to log a render perf, so don't do anything
                return;
            }

            // Trigger, Search workflow related signals for tracking reliability
            if (tableView.tableQuery.type === TableQueryType.Search) {
                triggerSearchSignals(tableView, loadState);
                return;
            }

            //Trigger, MailListRendered to track the LSR for app load times

            if (!emailContextValues.ECPath) {
                //LSR in ECPaths is logged separately
                LSRLogging(this.appContext);
            }

            // Trigger, PerceivedPerformance: MailListContentRender when we switch folders or exit search
            // Save this perf marker for the first load so that they are tracked by PAS
            const measure = safePerformanceMarkEnd('SelectFolderNodeToMailListContentRender');
            if (measure) {
                const folderTableQuery = tableView.tableQuery as MailFolderTableQuery;
                const folderId = folderTableQuery.folderId as DistinguishedFolderIdName;
                const distinguishedFolderId = folderId && folderIdToName(folderId);
                const mailListPerceivedPerformanceDataPoint = new PerformanceDatapoint(
                    'PerceivedPerformance: MailListContentRender',
                    { isCore: true }
                );

                let bootType: string;
                if (!firstLoadComplete) {
                    try {
                        bootType = await getBootType();
                    } catch {
                        // fail silently
                    }
                }

                mailListPerceivedPerformanceDataPoint.addCustomData({
                    startTime: Math.round(measure.startTime),
                    source: source,
                    loadingState: loadState,
                    tableQueryType: folderTableQuery ? folderTableQuery.type : 'No Type',
                    distinguishedFolderId: distinguishedFolderId,
                    firstLoadCompleteTime: firstLoadComplete
                        ? ''
                        : new Date().getTime() - (window.performance?.timing?.fetchStart ?? 0),
                    bootType: firstLoadComplete ? '' : bootType,
                });
                mailListPerceivedPerformanceDataPoint.end(Math.round(measure.duration));
                if (!firstLoadComplete) {
                    firstLoadComplete = true;
                }
            }
        } catch (error) {
            trace.warn(`Error in measureInitialLoadPerformance for MailList: ${error}`);
        }
    }

    /*
    We should show the message Ad list when all of the following conditions are true:
    0. Consumer user!
    1. The Ad list is already removed by the user
    2. For Other Inbox only flight, make sure the view is in Focused Other
    3. For all Inbox flight, make sure Folder is NOT drafts, sentitems, deleteditems, junkemail, archive, conversationhistory, notes
    4. Search does not run
    5. mini OWA in top-level frame (not inside OPX iframe)
    */
    private showNativeAdFormat = (tableView: TableView): NativeAdUserFlight => {
        if (!isConsumer()) {
            return NativeAdUserFlight.NoNativeAd;
        }

        if (getFocusedFilterForTable(tableView) === FocusedViewFilter.Focused) {
            return NativeAdUserFlight.NoNativeAd;
        }

        if (isOpx()) {
            return NativeAdUserFlight.NoNativeAd;
        }
        if (getStore() && getStore().listRemoved) {
            return NativeAdUserFlight.NoNativeAd;
        }

        if (
            tableView.tableQuery.folderId !== folderNameToId('drafts') &&
            tableView.tableQuery.folderId !== folderNameToId('sentitems') &&
            tableView.tableQuery.folderId !== folderNameToId('deleteditems') &&
            tableView.tableQuery.folderId !== folderNameToId('junkemail') &&
            tableView.tableQuery.folderId !== folderNameToId('conversationhistory') &&
            tableView.tableQuery.folderId !== folderNameToId('notes') &&
            tableView.tableQuery.folderId !== folderNameToId('archive') &&
            listViewStore != null &&
            !listViewStore.shouldShowSearchBox &&
            tableView.tableQuery.type !== TableQueryType.Search
        ) {
            return getNativeAdShowStatus();
        } else {
            return NativeAdUserFlight.NoNativeAd;
        }
    };

    private renderListViewContent(): JSX.Element {
        const tableView = getSelectedTableView();
        const richContentFlagsForUser: RichContentFlags = {
            shouldShowRSVPUnit: true,
        };

        return (
            <DisplayCustomisationsProvider richContentFlags={richContentFlagsForUser}>
                <VirtualizedMailList
                    showNativeAdFormat={this.showNativeAdFormat(tableView)}
                    forwardedRefs={{ switchRef: this.switchRef, filterRef: this.filterRef }}
                />
            </DisplayCustomisationsProvider>
        );
    }

    private renderEmptyViewContent(mailListViewState: any): JSX.Element {
        const tableView = getSelectedTableView();
        let showNativeAdContainer = NativeAdUserFlight.NoNativeAd;

        if (
            tableView &&
            this.showNativeAdFormat(tableView) !== NativeAdUserFlight.NoNativeAd &&
            mailListViewState === MailListViewState.Empty
        ) {
            showNativeAdContainer = getNativeAdShowStatus();
        }

        if (showNativeAdContainer !== NativeAdUserFlight.NoNativeAd) {
            resetGlobalAdState();
        }

        const preListComponent = (
            <>
                {showNativeAdContainer === NativeAdUserFlight.PrebidMobileBannerAd && (
                    <NativeAdContainer />
                )}
                {!(isInSearchView() && mailListViewState === MailListViewState.Blank) && (
                    <MailListHeader
                        forwardedRefs={{ switchRef: this.switchRef, filterRef: this.filterRef }}
                    />
                )}
                {!isInSearchView() && shouldShowAutomaticRepliesBanner(tableView) && (
                    <LazyAutomaticRepliesBanner />
                )}
                {getMailStore().showAd &&
                    showNativeAdContainer === NativeAdUserFlight.PrebidMobileAllInbox && (
                        <NativeAdContainer />
                    )}
            </>
        );
        return (
            <MailListEmptyState stateType={mailListViewState} preListComponent={preListComponent} />
        );
    }

    render() {
        const mailListViewState = getMailListViewState();
        if ([MailListViewState.Loaded, MailListViewState.Error].includes(mailListViewState)) {
            return this.renderListViewContent();
        } else {
            return this.renderEmptyViewContent(mailListViewState);
        }
    }
}
